import React, { useState } from "react";
import moment from "moment";
import gql from "graphql-tag";
import { useLazyQuery } from "@apollo/client";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import makeStyles from "@mui/styles/makeStyles";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

const SHARE_ACCESS_LOGS_QUERY = gql`
  query ShareAccessLogs($shareLinkId: ID!) {
    incidents {
      shareAccessLogs(shareLinkId: $shareLinkId) {
        id
        ipAddress
        attemptedAt
        action
      }
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  listItem: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  listItemContent: {
    flex: 1,
  },
  shareInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  shareInfoText: {
    fontWeight: 500,
  },
  dateText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  expiredChip: {
    marginTop: theme.spacing(0.5),
  },
  noShares: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
  },
  logTable: {
    "& .MuiTableCell-root": {
      padding: theme.spacing(0.75, 1.5),
      fontSize: "0.8rem",
      borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: theme.palette.grey[100],
      fontWeight: 600,
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      color: theme.palette.text.secondary,
    },
  },
  logRow: {
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "&:last-child .MuiTableCell-root": {
      borderBottom: "none",
    },
  },
}));

const ACTION_COLORS = {
  LinkExpired: { bg: "#f5f5f5", color: "#4d4d4d", label: "Link expired" },
  AutoResendLink: { bg: "#f5f5f5", color: "#161212", label: "Auto resend" },
  LinkOpened: { bg: "#e3f2fd", color: "#0a9911", label: "Link opened" },
  SessionInvalid: {
    bg: "#f8e9b6e8",
    color: "#e48e05",
    label: "Session invalid",
  },
  RequestNewLink: {
    bg: "#fcdeaf",
    color: "#ef6c00",
    label: "Request new link",
  },
};

const IncidentPropertyShare = ({ shares, onRevokeShare }) => {
  const classes = useStyles();
  const [logDialogShare, setLogDialogShare] = useState(null);
  const [fetchLogs, { data: logsData, loading: logsLoading }] = useLazyQuery(
    SHARE_ACCESS_LOGS_QUERY,
    { fetchPolicy: "network-only" },
  );

  const handleViewLogs = (share) => {
    setLogDialogShare(share);
    fetchLogs({ variables: { shareLinkId: share.id } });
  };

  const accessLogs = logsData?.incidents?.shareAccessLogs || [];

  if (!shares || shares.length === 0) {
    return (
      <Typography className={classes.noShares}>
        No shares yet. Create one above to share this incident.
      </Typography>
    );
  }

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;

    return moment(expiresAt).isBefore(moment());
  };

  return (
    <>
      <List>
        {shares.map((share, index) => {
          const expired = isExpired(share.expiresAt);

          return (
            <React.Fragment key={share.id}>
              {index > 0 && <Divider />}
              <ListItem className={classes.listItem}>
                <Box className={classes.listItemContent}>
                  <div className={classes.shareInfo}>
                    <Typography className={classes.shareInfoText}>
                      {share.emailShared}
                    </Typography>
                    <Typography className={classes.shareInfoText}>
                      {moment(share.createdAt).format("DD/MM/YYYY")}
                      {" - "}
                      {share.expiresAt
                        ? moment(share.expiresAt).format("DD/MM/YYYY")
                        : "No expiry"}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography className={classes.dateText}>
                        Last accessed:{" "}
                        {share.lastAccessedAt
                          ? moment(share.lastAccessedAt).format(
                              "DD/MM/YYYY HH:mm",
                            )
                          : "Never"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="primary"
                        onClick={() => handleViewLogs(share)}
                        sx={{
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.3,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        View history
                        <Icon
                          sx={{
                            fontSize: "14px !important",
                          }}
                        >
                          open_in_new
                        </Icon>
                      </Typography>
                    </Box>
                  </div>
                </Box>
                <Box className={classes.actionButtons}>
                  {share.revokedAt ? (
                    <Chip
                      label="Revoked"
                      color="error"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                      icon={<Icon>block</Icon>}
                    />
                  ) : expired ? (
                    <Chip
                      label="Expired"
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                      icon={<Icon>schedule</Icon>}
                    />
                  ) : (
                    <Tooltip title="Revoke share">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<Icon>undo</Icon>}
                        onClick={() => onRevokeShare && onRevokeShare(share.id)}
                      >
                        Revoke
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      <Dialog
        open={Boolean(logDialogShare)}
        onClose={() => setLogDialogShare(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Access logs:{" "}
          <span style={{ color: "#1565c0" }}>
            {logDialogShare?.emailShared}
          </span>
          <Typography
            variant="caption"
            sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
          >
            Created by: {logDialogShare?.createdByName} - Created on:{" "}
            {moment(logDialogShare?.createdAt).format("DD/MM/YYYY HH:mm")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : accessLogs.length === 0 ? (
            <Typography
              sx={{ textAlign: "center", py: 2, color: "text.secondary" }}
            >
              No access logs found.
            </Typography>
          ) : (
            <Table size="small" className={classes.logTable}>
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accessLogs.map((log) => {
                  const actionStyle = ACTION_COLORS[log.action] || {
                    bg: "#f5f5f5",
                    color: "#616161",
                    label: log.action,
                  };
                  return (
                    <TableRow key={log.id} className={classes.logRow}>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {moment(log.attemptedAt).format("DD/MM/YYYY HH:mm")}
                        </Box>
                      </TableCell>
                      <TableCell>{log.ipAddress || "-"}</TableCell>
                      <TableCell>
                        <Chip
                          label={actionStyle.label}
                          size="small"
                          sx={{
                            fontSize: "0.7rem",
                            height: 24,
                            fontWeight: 600,
                            backgroundColor: actionStyle.bg,
                            color: actionStyle.color,
                            border: "none",
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialogShare(null)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IncidentPropertyShare;
